/**
 * Tenant-scoped browser storage. Acts as a synchronous local cache; when a
 * backend session is active, writes are mirrored to MongoDB via a registered
 * cloud-persistence hook (see lib/cloudSync.ts) and reads can be hydrated from
 * the server on login.
 *
 * Keys are scoped by tenant id so each organization's data stays separate in the
 * browser. Clear via DevTools → Application → Local Storage, or use clearDevNamespace().
 */
import { getTenantId } from './tenant';
import { getActiveLedgerId, isViewAllLedgers } from './ledger';

export const PREFIX = 'trigovex_dev_';

/** Build the localStorage key for a raw storage key, scoped to tenant + ledger. */
export function tenantScopedKey(rawKey: string): string {
  const tid = getTenantId() ?? 'anon';
  const viewAll = isViewAllLedgers();
  const lid = viewAll ? 'all' : getActiveLedgerId() ?? 'default';
  return `${PREFIX}${tid}_${lid}_${rawKey}`;
}

function key(k: string) {
  return tenantScopedKey(k);
}

/** Optional bridge to server persistence. Registered at runtime by cloudSync. */
export type CloudPersistence = {
  put: (rawKey: string, value: unknown) => void;
  remove: (rawKey: string) => void;
};

let cloudPersistence: CloudPersistence | null = null;

export function registerCloudPersistence(writer: CloudPersistence | null): void {
  cloudPersistence = writer;
}

export function saveDevJson(storageKey: string, value: unknown): void {
  try {
    localStorage.setItem(key(storageKey), JSON.stringify(value));
  } catch {
    /* quota or private mode */
  }
  if (cloudPersistence) {
    try {
      cloudPersistence.put(storageKey, value);
    } catch {
      /* never let sync break local writes */
    }
  }
}

export function loadDevJson<T>(storageKey: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key(storageKey));
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function removeDevKey(storageKey: string): void {
  localStorage.removeItem(key(storageKey));
  if (cloudPersistence) {
    try {
      cloudPersistence.remove(storageKey);
    } catch {
      /* ignore */
    }
  }
}

export function appendDevLog(storageKey: string, entry: Record<string, unknown>, max = 80): void {
  const list = loadDevJson<Record<string, unknown>[]>(storageKey, []);
  list.push({ ...entry, _savedAt: new Date().toISOString() });
  saveDevJson(storageKey, list.slice(-max));
}

/** All raw storage keys currently cached for the active tenant (prefix stripped). */
export function listTenantRawKeys(): string[] {
  const tid = getTenantId() ?? 'anon';
  const prefix = `${PREFIX}${tid}_`;
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) out.push(k.slice(prefix.length));
    }
  } catch {
    /* ignore */
  }
  return out;
}

export function clearDevNamespace(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
